import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Plane, ArrowLeft, Mail, CheckCircle, KeyRound, ShieldCheck } from 'lucide-react';
import api from '@/lib/api';

export default function ForgotPasswordPage() {
  const [step, setStep] = useState('email'); // email | otp | done
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email) { toast.error('Enter your email'); return; }
    setLoading(true);
    try {
      const res = await api.post('/auth/forgot-password', { email });
      setStep('otp');
      if (res.data.email_sent) {
        toast.success('Reset code sent to your email');
      } else {
        toast.info('Check your email for reset code');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!otp || otp.length < 6) { toast.error('Enter the 6-digit code from your email'); return; }
    if (!newPassword || newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token: otp, new_password: newPassword });
      setStep('done');
      toast.success('Password reset successful');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md space-y-6 animate-fade-in">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-voyage-blue flex items-center justify-center">
            <Plane className="w-5 h-5 text-white" />
          </div>
          <span className="font-heading text-2xl font-bold tracking-tight text-foreground">VoyageOS</span>
        </div>

        {step === 'done' ? (
          <Card className="border border-border">
            <CardContent className="p-6 text-center space-y-4">
              <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/20 flex items-center justify-center mx-auto">
                <CheckCircle className="w-6 h-6 text-emerald-500" />
              </div>
              <h2 className="font-heading text-xl font-bold">Password Reset Complete</h2>
              <p className="text-sm text-muted-foreground">You can now login with your new password.</p>
              <Link to="/login">
                <Button className="w-full bg-voyage-blue hover:bg-voyage-blue-dark text-white mt-2" data-testid="back-to-login-btn">
                  Back to Login
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : step === 'otp' ? (
          <Card className="border border-border">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck className="w-5 h-5 text-voyage-orange" />
                <h2 className="font-heading text-lg font-bold">Enter Reset Code</h2>
              </div>
              <p className="text-sm text-muted-foreground">
                We've sent a 6-digit code to <span className="font-medium text-foreground">{email}</span>. Enter it below with your new password.
              </p>
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label>Reset Code</Label>
                  <Input
                    type="text"
                    placeholder="Enter 6-digit code"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="h-11 text-center text-lg tracking-[0.3em] font-mono"
                    maxLength={6}
                    data-testid="otp-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>New Password</Label>
                  <Input
                    type="password"
                    placeholder="Min 6 characters"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="h-11"
                    data-testid="new-password-input"
                  />
                </div>
                <Button type="submit" disabled={loading}
                  className="w-full h-11 bg-voyage-blue hover:bg-voyage-blue-dark text-white"
                  data-testid="reset-password-btn">
                  {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Reset Password'}
                </Button>
              </form>
              <button
                onClick={() => { setStep('email'); setOtp(''); setNewPassword(''); }}
                className="text-xs text-voyage-blue hover:underline w-full text-center"
                data-testid="resend-code-link">
                Didn't receive code? Try again
              </button>
            </CardContent>
          </Card>
        ) : (
          <Card className="border border-border">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <Mail className="w-5 h-5 text-voyage-blue" />
                <h2 className="font-heading text-lg font-bold">Forgot Password</h2>
              </div>
              <p className="text-sm text-muted-foreground">Enter your registered email to receive a password reset code.</p>
              <form onSubmit={handleRequestReset} className="space-y-4">
                <div className="space-y-2">
                  <Label>Email Address</Label>
                  <Input
                    type="email"
                    placeholder="you@agency.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-11"
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button type="submit" disabled={loading}
                  className="w-full h-11 bg-voyage-blue hover:bg-voyage-blue-dark text-white"
                  data-testid="send-reset-btn">
                  {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Send Reset Code'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        <p className="text-sm text-center">
          <Link to="/login" className="text-voyage-blue hover:underline flex items-center justify-center gap-1" data-testid="back-link">
            <ArrowLeft className="w-3 h-3" /> Back to Login
          </Link>
        </p>
      </div>
    </div>
  );
}
